import React from 'react';
import { Plus, Edit2, Trash2, Tag, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TabularNumber } from '@/components/common/TabularNumber';
import { SettlementAdjustmentRecord } from '@/types/database';
import { SettlementLineItem } from '@/engine/settlement_calculator';
import { formatCurrency, formatDate } from '@/lib/utils';
import { getAccountName } from '@/lib/chartOfAccounts';

interface SettlementAdjustmentsGridProps {
  adjustments: SettlementAdjustmentRecord[];
  calculationLines?: SettlementLineItem[];
  isFrozen: boolean;
  onAddClick: () => void;
  onEditClick: (adj: SettlementAdjustmentRecord) => void;
  onVoidClick: (adj: SettlementAdjustmentRecord) => void;
}

export const SettlementAdjustmentsGrid: React.FC<SettlementAdjustmentsGridProps> = ({
  adjustments,
  calculationLines,
  isFrozen,
  onAddClick,
  onEditClick,
  onVoidClick,
}) => {
  // Map calculation lines by reference_id to obtain engine-authoritative signs & line_type
  const linesByRefId = new Map<string, SettlementLineItem>();
  if (calculationLines) {
    for (const line of calculationLines) {
      if (line.reference_id) {
        linesByRefId.set(line.reference_id, line);
      }
    }
  }

  const activeAdjustments = adjustments.filter((a) => !a.is_void);

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-1 border-b border-slate-200">
        <div>
          <h4 className="text-sm font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <span>4. Grower Adjustments (Accounts 4010 – 4050)</span>
            <span className="text-xs font-normal text-slate-500">
              ({activeAdjustments.length} {activeAdjustments.length === 1 ? 'record' : 'records'})
            </span>
          </h4>
          <p className="text-xs text-slate-500">
            Post-profit adjustments, cash advances, retail deductions, used sack credits, and raiser reimbursements.
          </p>
        </div>
        {!isFrozen && (
          <Button
            size="sm"
            onClick={onAddClick}
            className="h-8 px-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs shadow-sm self-start sm:self-auto"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Adjustment
          </Button>
        )}
      </div>

      {activeAdjustments.length === 0 ? (
        <div className="p-6 text-center border border-dashed border-slate-200 rounded-lg bg-slate-50/50">
          <p className="text-xs text-slate-500">No post-profit adjustments booked for this cycle.</p>
          {!isFrozen && (
            <Button
              variant="outline"
              size="sm"
              onClick={onAddClick}
              className="mt-2 text-xs h-7 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
            >
              <Plus className="w-3 h-3 mr-1" /> Add First Adjustment
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 shadow-sm">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
              <tr>
                <th className="py-2.5 px-3">COA Account</th>
                <th className="py-2.5 px-3">Date</th>
                <th className="py-2.5 px-3">Description</th>
                <th className="py-2.5 px-3 text-right">Qty</th>
                <th className="py-2.5 px-3 text-right">Unit Rate</th>
                <th className="py-2.5 px-3 text-right">Amount (₱)</th>
                {!isFrozen && <th className="py-2.5 px-3 text-center w-20">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {activeAdjustments.map((adj) => {
                const engineLine = linesByRefId.get(adj.id);
                // Treatment strictly derived from calculation engine line or database definition
                const isDeduction = engineLine ? engineLine.is_deduction : adj.account_code !== '4050';
                const signText = isDeduction ? '–' : '+';
                const badgeColor = isDeduction
                  ? 'bg-rose-50 text-rose-700 border-rose-200'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200';

                return (
                  <tr key={adj.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-2.5 px-3">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono font-bold text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                            {adj.account_code}
                          </span>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${badgeColor}`}>
                            {isDeduction ? 'Deduction' : 'Credit'}
                          </span>
                        </div>
                        <span className="text-[10px] text-slate-500 font-medium">
                          {getAccountName(adj.account_code)}
                        </span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-slate-600 whitespace-nowrap">
                      {formatDate(adj.adjustment_date)}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-medium text-slate-900">{adj.description}</div>
                      {adj.rate_rules && (
                        <div className="flex items-center gap-1 text-[11px] text-indigo-600 mt-0.5">
                          <Tag className="w-3 h-3" />
                          <span>Rule: {adj.rate_rules.rule_name}</span>
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right text-slate-700 font-mono">
                      {adj.quantity !== null && adj.quantity !== undefined ? (
                        <TabularNumber value={adj.quantity} decimals={0} />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right text-slate-700 font-mono">
                      {adj.unit_rate !== null && adj.unit_rate !== undefined ? (
                        formatCurrency(adj.unit_rate)
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-bold whitespace-nowrap">
                      <span className={isDeduction ? 'text-rose-700' : 'text-emerald-700'}>
                        {signText} {formatCurrency(Math.abs(adj.amount))}
                      </span>
                    </td>
                    {!isFrozen && (
                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditClick(adj)}
                            className="h-7 w-7 p-0 text-slate-500 hover:text-indigo-600"
                            title="Edit Adjustment"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onVoidClick(adj)}
                            className="h-7 w-7 p-0 text-slate-500 hover:text-rose-600"
                            title="Void Adjustment"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
